import React, { useEffect, useMemo, useState } from "react"
import ReactApexChart from "react-apexcharts"
import { useQuery } from "@apollo/client"
import { PPDefaultSelector } from "../../components/ui/PPDefaultSelector"
import { uniqBy, keyBy } from "../../utils/collectionUtils"
import countries from "i18n-iso-countries"
import PPStatisticsInsights from "../../utils/PPStatisticsInsights"
import { useTranslation } from "react-i18next"
import useRequireAuth from "../../hooks/use-require-auth"
import { CATEGORY_TREND_QUERY } from "./statisticsQueries"
import { MultiValueLabel, Option } from "../../components/pricetable/PPShopPreset"
import {
    COLORS,
    DEFAULT_OVERVIEW_SHOP_COUNT,
    barHeight,
    AnimatedBarGrid,
    useLocalLangFilter,
    LangSelector,
    SingleValueWithFlag,
    buildShopOptions,
    SHOP_MULTI_SELECT_COMPONENTS
} from "./statisticsShared"

function MarketActivitySummary({ shopStats, trendByShop, t }) {
    const shopOptions = useMemo(() => buildShopOptions(shopStats, []), [shopStats])
    const [selectedShops, setSelectedShops] = useState(shopOptions.slice(0, 3))
    const activityByShopId = useMemo(() => {
        const ranking = PPStatisticsInsights.priceActivityByShop(trendByShop)
        return keyBy(ranking, (r) => r.shop.shopId)
    }, [trendByShop])

    useEffect(() => {
        if (shopOptions.length > 0 && selectedShops.length === 0) {
            setSelectedShops(shopOptions.slice(0, 3))
        }
    }, [shopOptions])

    const handleChange = (newVal) => {
        setSelectedShops(newVal || [])
    }

    const shopRows = selectedShops.map(opt => {
        const activity = activityByShopId[opt.value]
        const changes = activity?.changes || 0
        const mapped = activity?.mapped || 0
        const pct = activity?.per100 || 0
        return { opt, changes, mapped, pct }
    })

    return (
        <div className="card">
            <div className="card-body">
                <div className="row align-items-center mb-3">
                    <div className="col">
                        <h3 className="card-title mb-0">{t("statistic.market_summary_title")}</h3>
                    </div>
                    <div className="col-auto">
                        {shopOptions.length > 0 && (
                            <PPDefaultSelector
                                id="selectMarketActivityShop"
                                isMulti={true}
                                isClearable={true}
                                closeMenuOnSelect={false}
                                onChange={handleChange}
                                data={shopOptions}
                                value={selectedShops}
                                components={SHOP_MULTI_SELECT_COMPONENTS}
                                placeholder={t("select") + "..."}
                            />
                        )}
                    </div>
                </div>
                {shopRows.length === 0 ? (
                    <div className="pp-stats-empty">–</div>
                ) : (
                    <div className="pp-stats-market-grid">
                        {shopRows.map(({ opt, pct, changes, mapped }) => (
                            <div key={opt.value} className="pp-stats-market-shop-card">
                                <div className="market-shop-name">
                                    {opt.lang && <span className={"fi fi-" + opt.lang} style={{ marginRight: "6px" }} />}
                                    {opt.label.replace(` (${(opt.lang || "").toUpperCase()})`, "")}
                                </div>
                                <div className="market-shop-stats">
                                    <div className="market-stat">
                                        <span className="stat-label">{t("statistic.market_products_changed_short")}</span>
                                        <span className="stat-value">{pct}%</span>
                                    </div>
                                    <div className="market-stat">
                                        <span className="stat-label">{t("statistic.market_total_changes_short")}</span>
                                        <span className="stat-value">{changes.toLocaleString()}</span>
                                    </div>
                                    <div className="market-stat">
                                        <span className="stat-label">{t("statistic.market_mapped_short")}</span>
                                        <span className="stat-value">{mapped.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

function CompetitorCheaperChart({ productsPriceLowerByShops, shops, i18n, t }) {
    const [localLangFilter, setLocalLangFilter] = useState([])
    const filtered = PPStatisticsInsights.filterByLang(productsPriceLowerByShops || [], localLangFilter)
        .filter((s) => s.count > 0)
        .sort((a, b) => b.percentage - a.percentage)
        .slice(0, 6)
    const colorById = keyBy(shops || [], "id")

    return (
        <div className="card h-100">
            <div className="card-body">
                <div className="row align-items-center mb-3">
                    <div className="col">
                        <h3 className="card-title mb-0">{t("statistic.competitor_cheaper_title")}</h3>
                    </div>
                    <div className="col-auto">
                        <LangSelector
                            id="selectCompetitorCheaperLang"
                            data={productsPriceLowerByShops}
                            langFilter={localLangFilter}
                            setLangFilter={setLocalLangFilter}
                            i18n={i18n}
                        />
                    </div>
                </div>
                {filtered.length === 0 ? (
                    <div className="pp-stats-empty">–</div>
                ) : (
                    <AnimatedBarGrid itemKeys={filtered.map(s => `${s.id}-${s.lang}`)}>
                        {(isReady) => (
                            <div className="pp-stats-competitor-cheaper">
                                {filtered.map((s, idx) => {
                                    const shopColor = colorById[s.id]?.colorHex || COLORS.NeutralStart
                                    const key = `${s.id}-${s.lang}`
                                    return (
                                        <div key={key} className="competitor-row">
                                            <div className="competitor-shop">
                                                <span className={"fi fi-" + s.lang} style={{ marginRight: "6px" }} />
                                                {s.name}
                                            </div>
                                            <div className="competitor-bar-wrap">
                                                <div 
                                                    className="competitor-bar"
                                                    title={`${Math.round(s.percentage)}%`}
                                                    style={{ 
                                                        width: `${s.percentage}%`,
                                                        transform: isReady(key) ? "scaleX(1)" : "scaleX(0)",
                                                        background: shopColor,
                                                        transitionDelay: `${idx * 30}ms`
                                                    }}
                                                >
                                                    <span className="competitor-pct">{Math.round(s.percentage)}%</span>
                                                </div>
                                            </div>
                                            <div className="competitor-count">
                                                {s.count} / {s.totalCount}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </AnimatedBarGrid>
                )}
            </div>
        </div>
    )
}

function PriceBalancePanel({ shopStats, gaps, i18n, t, preferredLang }) {
    const [langFilter, setLangFilter] = useLocalLangFilter(shopStats, i18n, preferredLang)
    const filteredShops = PPStatisticsInsights.filterByLang(
        PPStatisticsInsights.competitorShops(shopStats),
        langFilter
    )
    const filteredGaps = PPStatisticsInsights.filterByLang(gaps || [], langFilter)
    const rows = PPStatisticsInsights.shopPriceBalances(filteredShops, filteredGaps)

    return (
        <div className="card h-100">
            <div className="card-body">
                <div className="row align-items-center mb-3">
                    <div className="col">
                        <h3 className="card-title mb-0">{t("statistic.balance_title")}</h3>
                    </div>
                    <div className="col-auto">
                        <LangSelector
                            id="selectStatsBalanceLang"
                            data={shopStats}
                            langFilter={langFilter}
                            setLangFilter={setLangFilter}
                            i18n={i18n}
                        />
                    </div>
                </div>
                {rows.length === 0 ? (
                    <div className="pp-stats-empty">{t("statistic.empty_gaps")}</div>
                ) : (
                    <AnimatedBarGrid itemKeys={rows.map(r => `${r.shop.shopId}-${r.shop.lang}`)}>
                        {(isReady) => (
                            <div className="pp-stats-balance-list">
                                {rows.map((row, rowIdx) => {
                                    const key = `${row.shop.shopId}-${row.shop.lang}`
                                    const ready = isReady(key)
                                    return (
                                        <div className="pp-stats-balance-row" key={key}>
                                            <div className="pp-stats-balance-head">
                                                <strong>{PPStatisticsInsights.shopLabel(row.shop)}</strong>
                                                <span className="meta">
                                                    {row.total.toLocaleString()} {t("statistic.trend_products")}
                                                </span>
                                            </div>
                                            <div className="pp-stats-balance-bar" aria-hidden="true">
                                                <div
                                                    className="seg cheaper"
                                                    style={{ width: `${row.cheaperShare}%`, transform: ready ? "scaleX(1)" : "scaleX(0)", transitionDelay: `${rowIdx * 30}ms` }}
                                                    title={`${row.cheaperShare.toFixed(0)}%`}
                                                >
                                                    {row.cheaperShare >= 10 && <span>{Math.round(row.cheaperShare)}%</span>}
                                                </div>
                                                <div
                                                    className="seg equal"
                                                    style={{ width: `${row.equalShare}%`, transform: ready ? "scaleX(1)" : "scaleX(0)", transitionDelay: `${rowIdx * 30 + 10}ms` }}
                                                    title={`${row.equalShare.toFixed(0)}%`}
                                                >
                                                    {row.equalShare >= 10 && <span>{Math.round(row.equalShare)}%</span>}
                                                </div>
                                                <div
                                                    className="seg expensive"
                                                    style={{ width: `${row.expensiveShare}%`, transform: ready ? "scaleX(1)" : "scaleX(0)", transitionDelay: `${rowIdx * 30 + 20}ms` }}
                                                    title={`${row.expensiveShare.toFixed(0)}%`}
                                                >
                                                    {row.expensiveShare >= 10 && <span>{Math.round(row.expensiveShare)}%</span>}
                                                </div>
                                            </div>
                                            <div className="pp-stats-balance-meta">
                                                <span className="cheaper-text">
                                                    {Math.round(row.cheaperShare)}% {t("statistic.balance_cheaper")}
                                                    {row.avgGapCheaper > 0
                                                        ? ` · Ø ${PPStatisticsInsights.formatEuro(row.avgGapCheaper)}`
                                                        : ""}
                                                </span>
                                                <span className="expensive-text">
                                                    {Math.round(row.expensiveShare)}% {t("statistic.balance_expensive")}
                                                    {row.avgGapExpensive > 0
                                                        ? ` · Ø ${PPStatisticsInsights.formatEuro(row.avgGapExpensive)}`
                                                        : ""}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </AnimatedBarGrid>
                )}
            </div>
        </div>
    )
}

function StackedShopChart({ title, shops, seriesBuilder, id, colors }) {
    const allShops = shops || []
    const shopOptions = useMemo(() => allShops.map((s) => ({
        label: `${s.name || s.shortName || ""} (${(s.lang || "").toUpperCase()})`,
        value: `${s.shopId}-${s.lang}`,
        shopId: s.shopId,
        lang: s.lang
    })), [JSON.stringify(allShops.map(s => `${s.shopId}-${s.lang}`))])

    const [selectedShops, setSelectedShops] = useState(() => shopOptions.slice(0, DEFAULT_OVERVIEW_SHOP_COUNT))

    useEffect(() => {
        const allowed = new Set(shopOptions.map((option) => option.value))
        setSelectedShops((current) => {
            const stillValid = (current || []).filter((shop) => allowed.has(shop.value))
            if (stillValid.length > 0) {
                return stillValid
            }
            return shopOptions.slice(0, DEFAULT_OVERVIEW_SHOP_COUNT)
        })
    }, [shopOptions])

    const selectedKeys = (selectedShops || []).map((s) => s.value)
    const filtered = allShops.filter((s) => selectedKeys.includes(`${s.shopId}-${s.lang}`))
    const series = seriesBuilder(filtered)
    
    const barColors = colors || [COLORS.GreenStart, COLORS.YellowStart, COLORS.RedStart]
    const seriesNames = series.map(s => s.name)
    
    const shopData = filtered.map((shop, idx) => {
        const values = series.map(s => s.data[idx]?.y || 0)
        const total = values.reduce((a, b) => a + b, 0)
        const pcts = values.map(v => total > 0 ? Math.round((v / total) * 100) : 0)
        return { shop, values, total, pcts }
    })
    
    return (
        <div className="card h-100">
            <div className="card-body">
                <div className="row align-items-center mb-2">
                    <div className="col">
                        <h3 className="card-title mb-0">{title}</h3>
                    </div>
                    <div className="col-auto">
                        {shopOptions.length > 0 && (
                            <PPDefaultSelector
                                id={id}
                                isMulti={true}
                                closeMenuOnSelect={false}
                                onChange={setSelectedShops}
                                data={shopOptions}
                                value={selectedShops}
                                components={SHOP_MULTI_SELECT_COMPONENTS}
                            />
                        )}
                    </div>
                </div>
                {filtered.length === 0 ? (
                    <div className="pp-stats-empty">–</div>
                ) : (
                    <>
                        <div className="pp-stats-stacked-legend">
                            {seriesNames.map((name, i) => (
                                <span key={name} className="legend-item">
                                    <span className="dot" style={{ background: barColors[i] }}></span> {name}
                                </span>
                            ))}
                        </div>
                        <AnimatedBarGrid itemKeys={shopData.map(r => `${r.shop.shopId}-${r.shop.lang}`)}>
                            {(isReady) => (
                                <div className="pp-stats-stacked-list">
                                    {shopData.map((row, rowIdx) => {
                                        const key = `${row.shop.shopId}-${row.shop.lang}`
                                        const ready = isReady(key)
                                        return (
                                            <div key={key} className="stacked-row">
                                                <div className="stacked-shop">
                                                    {row.shop.lang && <span className={"fi fi-" + row.shop.lang} style={{ marginRight: "6px" }} />}
                                                    {PPStatisticsInsights.shopLabel(row.shop)}
                                                </div>
                                                <div className="stacked-bar-wrap">
                                                    {row.pcts.map((pct, i) => (
                                                        <div
                                                            key={i}
                                                            className="stacked-seg"
                                                            title={`${pct}%`}
                                                            style={{
                                                                width: `${pct}%`,
                                                                transform: ready ? "scaleX(1)" : "scaleX(0)",
                                                                background: barColors[i],
                                                                transitionDelay: `${rowIdx * 25 + i * 10}ms`
                                                            }}
                                                        >
                                                            {pct >= 12 && <span className="seg-label">{pct}%</span>}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="stacked-total">{row.total.toLocaleString()}</div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </AnimatedBarGrid>
                    </>
                )}
            </div>
        </div>
    )
}

function VolatilityChart({ trendByShop, shops, i18n, t }) {
    const [localLangFilter, setLocalLangFilter] = useState([])
    const filteredTrends = PPStatisticsInsights.filterByLang(trendByShop || [], localLangFilter)
    const ranking = PPStatisticsInsights.volatilityRanking(filteredTrends)
    const colorById = keyBy(shops || [], "id")
    const fallbackColors = [COLORS.NeutralStart, COLORS.GreenStart, COLORS.OrangeStart, COLORS.YellowStart, COLORS.RedStart, COLORS.GreyLight]

    const pctData = ranking.map((r, idx) => {
        const pct = Math.min(r.per100, 100)
        const color = colorById[r.shop.shopId]?.colorHex || fallbackColors[idx % fallbackColors.length]
        return { ...r, pct, color }
    })

    return (
        <div className="card h-100">
            <div className="card-body">
                <div className="row align-items-center mb-2">
                    <div className="col">
                        <h3 className="card-title mb-0">{t("statistic.volatility_title")}</h3>
                    </div>
                    <div className="col-auto">
                        <LangSelector
                            id="selectStatsVolatilityLang"
                            data={trendByShop}
                            langFilter={localLangFilter}
                            setLangFilter={setLocalLangFilter}
                            i18n={i18n}
                        />
                    </div>
                </div>
                {pctData.length === 0 || pctData.every((r) => r.changes === 0) ? (
                    <div className="pp-stats-empty">{t("statistic.empty_volatility")}</div>
                ) : (
                    <AnimatedBarGrid itemKeys={pctData.map(r => `${r.shop.shopId}-${r.shop.lang}`)}>
                        {(isReady) => (
                            <div className="pp-stats-volatility-grid">
                                {pctData.map((r, idx) => {
                                    const key = `${r.shop.shopId}-${r.shop.lang}`
                                    return (
                                        <div key={key} className="pp-stats-volatility-row">
                                            <div className="vol-shop">{PPStatisticsInsights.shopLabel(r.shop)}</div>
                                            <div className="vol-bar-wrap">
                                                <div
                                                    className="vol-bar"
                                                    title={`${r.pct}%`}
                                                    style={{
                                                        width: `${r.pct}%`,
                                                        transform: isReady(key) ? "scaleX(1)" : "scaleX(0)",
                                                        background: r.color,
                                                        transitionDelay: `${idx * 30}ms`
                                                    }}
                                                >
                                                    <span className="vol-pct">{r.pct}%</span>
                                                </div>
                                            </div>
                                            <div className="vol-detail">
                                                {r.changes.toLocaleString()} / {r.mapped.toLocaleString()}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </AnimatedBarGrid>
                )}
            </div>
        </div>
    )
}

function CategoryRisingChart({ shopStats, department, t }) {
    const shopOptions = useMemo(() => buildShopOptions(shopStats, []), [shopStats])
    const [selectedShop, setSelectedShop] = useState(null)

    useEffect(() => {
        if (shopOptions.length > 0 && !shopOptions.find((o) => o.value === selectedShop?.value)) {
            setSelectedShop(shopOptions[0])
        }
    }, [shopOptions, selectedShop])

    const shopIds = selectedShop?.value != null ? [selectedShop.value] : []

    const { data: trendData, loading } = useQuery(CATEGORY_TREND_QUERY, {
        fetchPolicy: "no-cache",
        variables: {
            department: department || "",
            shopIds: shopIds.length > 0 ? shopIds : null,
            trendDays: 7
        },
        skip: shopIds.length === 0
    })

    const trendByCategory = trendData?.stats?.priceTrendStatistics?.trendByCategory || []
    const hotspots = PPStatisticsInsights.categoryHotspots(trendByCategory, 6)
    const risingData = [...hotspots].sort((a, b) => b.risingShare - a.risingShare)

    const getCategoryTrendUrl = (categoryName) => {
        const safeName = (categoryName || "").replace(/\//g, "||")
        return `/pricetable/${encodeURIComponent(safeName)}/Kategorie/generic/priceTrendRising`
    }

    return (
        <div className="card h-100">
            <div className="card-body pp-stats-category-card">
                <div className="row align-items-center mb-2">
                    <div className="col">
                        <h3 className="card-title pp-stats-category-title mb-0">{t("statistic.trend_prices_up")}</h3>
                    </div>
                    <div className="col-auto">
                        {shopOptions.length > 0 && (
                            <PPDefaultSelector
                                id="selectCategoryRisingShop"
                                isMulti={false}
                                isClearable={false}
                                onChange={setSelectedShop}
                                data={shopOptions}
                                value={selectedShop}
                                components={{ SingleValue: SingleValueWithFlag, Option }}
                            />
                        )}
                    </div>
                </div>
                {loading ? (
                    <div className="pp-stats-empty">…</div>
                ) : risingData.length === 0 ? (
                    <div className="pp-stats-empty">{t("statistic.empty_hotspots")}</div>
                ) : (
                    <AnimatedBarGrid itemKeys={risingData.map(h => h.categoryName)}>
                        {(isReady) => (
                            <div className="pp-stats-category-list">
                                {risingData.map((h, idx) => (
                                    <div key={h.categoryName} className="category-row">
                                        <a
                                            href={getCategoryTrendUrl(h.categoryName)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="category-link"
                                        >
                                            {h.categoryName}
                                        </a>
                                        <div className="category-bar-container">
                                            <div 
                                                className="category-bar rising"
                                                title={`${Math.round(h.risingShare)}%`}
                                                style={{ 
                                                    width: `${h.risingShare}%`,
                                                    transform: isReady(h.categoryName) ? "scaleX(1)" : "scaleX(0)",
                                                    transitionDelay: `${idx * 30}ms`
                                                }}
                                            >
                                                <span className="bar-label">{Math.round(h.risingShare)}%</span>
                                            </div>
                                        </div>
                                        <div className="category-count">{h.risingCount} / {h.totalCount}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </AnimatedBarGrid>
                )}
            </div>
        </div>
    )
}

function CategoryFallingChart({ shopStats, department, t }) {
    const shopOptions = useMemo(() => buildShopOptions(shopStats, []), [shopStats])
    const [selectedShop, setSelectedShop] = useState(null)

    useEffect(() => {
        if (shopOptions.length > 0 && !shopOptions.find((o) => o.value === selectedShop?.value)) {
            setSelectedShop(shopOptions[0])
        }
    }, [shopOptions, selectedShop])

    const shopIds = selectedShop?.value != null ? [selectedShop.value] : []

    const { data: trendData, loading } = useQuery(CATEGORY_TREND_QUERY, {
        fetchPolicy: "no-cache",
        variables: {
            department: department || "",
            shopIds: shopIds.length > 0 ? shopIds : null,
            trendDays: 7
        },
        skip: shopIds.length === 0
    })

    const trendByCategory = trendData?.stats?.priceTrendStatistics?.trendByCategory || []
    const hotspots = PPStatisticsInsights.categoryHotspots(trendByCategory, 6)
    const fallingData = [...hotspots].sort((a, b) => b.fallingShare - a.fallingShare)

    const getCategoryTrendUrl = (categoryName) => {
        const safeName = (categoryName || "").replace(/\//g, "||")
        return `/pricetable/${encodeURIComponent(safeName)}/Kategorie/generic/priceTrendFalling`
    }

    return (
        <div className="card h-100">
            <div className="card-body pp-stats-category-card">
                <div className="row align-items-center mb-2">
                    <div className="col">
                        <h3 className="card-title pp-stats-category-title mb-0">{t("statistic.trend_prices_down")}</h3>
                    </div>
                    <div className="col-auto">
                        {shopOptions.length > 0 && (
                            <PPDefaultSelector
                                id="selectCategoryFallingShop"
                                isMulti={false}
                                isClearable={false}
                                onChange={setSelectedShop}
                                data={shopOptions}
                                value={selectedShop}
                                components={{ SingleValue: SingleValueWithFlag, Option }}
                            />
                        )}
                    </div>
                </div>
                {loading ? (
                    <div className="pp-stats-empty">…</div>
                ) : fallingData.length === 0 ? (
                    <div className="pp-stats-empty">{t("statistic.empty_hotspots")}</div>
                ) : (
                    <AnimatedBarGrid itemKeys={fallingData.map(h => h.categoryName)}>
                        {(isReady) => (
                            <div className="pp-stats-category-list">
                                {fallingData.map((h, idx) => (
                                    <div key={h.categoryName} className="category-row">
                                        <a
                                            href={getCategoryTrendUrl(h.categoryName)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="category-link"
                                        >
                                            {h.categoryName}
                                        </a>
                                        <div className="category-bar-container">
                                            <div 
                                                className="category-bar falling"
                                                title={`${Math.round(h.fallingShare)}%`}
                                                style={{ 
                                                    width: `${h.fallingShare}%`,
                                                    transform: isReady(h.categoryName) ? "scaleX(1)" : "scaleX(0)",
                                                    transitionDelay: `${idx * 30}ms`
                                                }}
                                            >
                                                <span className="bar-label">{Math.round(h.fallingShare)}%</span>
                                            </div>
                                        </div>
                                        <div className="category-count">{h.fallingCount} / {h.totalCount}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </AnimatedBarGrid>
                )}
            </div>
        </div>
    )
}

function TrendDistributionDonut({ trendByShop, t }) {
    const allShops = trendByShop || []
    const shopOptions = allShops.map((s) => ({
        label: `${s.shopName || ""} (${(s.lang || "").toUpperCase()})`,
        value: `${s.shopId}-${s.lang}`,
        shopId: s.shopId,
        lang: s.lang
    }))
    const [selectedShop, setSelectedShop] = useState(shopOptions[0] || null)

    useEffect(() => {
        if (shopOptions.length > 0 && !shopOptions.find(o => o.value === selectedShop?.value)) {
            setSelectedShop(shopOptions[0])
        }
    }, [shopOptions, selectedShop])

    const shopData = allShops.find((s) => `${s.shopId}-${s.lang}` === selectedShop?.value)
    const rising = shopData?.risingCount || 0
    const falling = shopData?.fallingCount || 0
    const unchanged = shopData?.unchangedCount || 0
    const total = shopData?.totalCount || (rising + falling + unchanged)

    if (!shopData || total === 0) {
        return (
            <div className="card h-100">
                <div className="card-body">
                    <div className="row align-items-center mb-2">
                        <div className="col">
                            <h3 className="card-title mb-0">{t("statistic.trend_distribution_title")}</h3>
                        </div>
                        <div className="col-auto">
                            {shopOptions.length > 0 && (
                                <PPDefaultSelector
                                    id="selectStatsTrendShop"
                                    isMulti={false}
                                    isClearable={false}
                                    onChange={setSelectedShop}
                                    data={shopOptions}
                                    value={selectedShop}
                                    components={{ SingleValue: SingleValueWithFlag, Option }}
                                />
                            )}
                        </div>
                    </div>
                    <div className="pp-stats-empty">{t("statistic.empty_hotspots")}</div>
                </div>
            </div>
        )
    }

    const series = [rising, falling, unchanged]
    const options = {
        labels: [
            t("statistic.trend_prices_up"),
            t("statistic.trend_prices_down"),
            t("statistic.trend_unchanged")
        ],
        colors: [COLORS.GreenStart, COLORS.RedStart, COLORS.GreyLight],
        chart: { type: "donut" },
        legend: { position: "bottom" },
        dataLabels: {
            enabled: true,
            formatter: (val) => `${val.toFixed(0)}%`,
            style: { colors: ["#fff"], fontWeight: "bold" },
            dropShadow: { enabled: true, top: 1, left: 1, blur: 1, opacity: 0.8 }
        },
        plotOptions: {
            pie: {
                donut: {
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: t("statistic.trend_products"),
                            formatter: () => total.toLocaleString()
                        }
                    }
                }
            }
        }
    }

    return (
        <div className="card h-100">
            <div className="card-body">
                <div className="row align-items-center mb-2">
                    <div className="col">
                        <h3 className="card-title mb-0">{t("statistic.trend_distribution_title")}</h3>
                    </div>
                    <div className="col-auto">
                        {shopOptions.length > 0 && (
                            <PPDefaultSelector
                                id="selectStatsTrendShop"
                                isMulti={false}
                                isClearable={false}
                                onChange={setSelectedShop}
                                data={shopOptions}
                                value={selectedShop}
                                components={{ SingleValue: SingleValueWithFlag, Option }}
                            />
                        )}
                    </div>
                </div>
                <ReactApexChart options={options} series={series} type="donut" height={300} />
            </div>
        </div>
    )
}

function StockHistoryChart({ history, shopStats, i18n, t, preferredLang }) {
    const langSource = (shopStats && shopStats.length > 0) ? shopStats : history
    const [localLangFilter, setLocalLangFilter] = useLocalLangFilter(langSource, i18n, preferredLang)
    const byShop = PPStatisticsInsights.stockHistoryByShopSeries(history, localLangFilter)
    const tickAmount = Math.min(byShop.shortDates.length, 8)

    const greenShareOptions = {
        chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
        stroke: { width: 3, curve: "smooth" },
        markers: { size: 0, hover: { size: 5 } },
        xaxis: {
            categories: byShop.shortDates,
            tickAmount,
            labels: {
                rotate: 0,
                hideOverlappingLabels: true,
                style: { fontSize: "11px" }
            }
        },
        yaxis: {
            min: 0,
            max: 100,
            title: { text: t("statistic.stock_available_axis"), style: { fontSize: "12px" } },
            labels: { formatter: (v) => `${v}%` }
        },
        legend: { position: "bottom" },
        tooltip: {
            y: { formatter: (v) => (v == null ? "–" : `${v}% ${t("statistic.stock_available")}`) }
        },
        grid: { strokeDashArray: 4 }
    }

    return (
        <div className="card">
            <div className="card-body">
                <div className="row align-items-center mb-2">
                    <div className="col">
                        <h3 className="card-title mb-0">{t("statistic.stock_history_title")}</h3>
                    </div>
                    <div className="col-auto">
                        <LangSelector
                            id="selectStatsStockHistLang"
                            data={shopStats}
                            langFilter={localLangFilter}
                            setLangFilter={setLocalLangFilter}
                            i18n={i18n}
                        />
                    </div>
                </div>
                {byShop.series.length === 0 ? (
                    <div className="pp-stats-empty">{t("statistic.empty_stock_history")}</div>
                ) : (
                    <ReactApexChart
                        options={greenShareOptions}
                        series={byShop.series}
                        type="line"
                        height={320}
                    />
                )}
            </div>
        </div>
    )
}

function OpportunityPanel({ shopStats, t }) {
    const allShops = PPStatisticsInsights.competitorShops(shopStats)
    const shopOptions = useMemo(() => allShops.map((s) => ({
        label: `${s.name || s.shortName || "?"} (${(s.lang || "").toUpperCase()})`,
        value: `${s.shopId}-${s.lang}`,
        shopId: s.shopId,
        lang: s.lang
    })), [JSON.stringify(allShops.map(s => `${s.shopId}-${s.lang}`))])
    
    const [selectedShops, setSelectedShops] = useState(() => shopOptions.slice(0, DEFAULT_OVERVIEW_SHOP_COUNT))

    useEffect(() => {
        const allowed = new Set(shopOptions.map((option) => option.value))
        setSelectedShops((current) => {
            const stillValid = (current || []).filter((shop) => allowed.has(shop.value))
            if (stillValid.length > 0) {
                return stillValid
            }
            return shopOptions.slice(0, DEFAULT_OVERVIEW_SHOP_COUNT)
        })
    }, [shopOptions])

    const selectedKeys = (selectedShops || []).map((s) => s.value)
    const filtered = allShops.filter((s) => selectedKeys.includes(`${s.shopId}-${s.lang}`))
    const opps = PPStatisticsInsights.availabilityOpportunities(filtered)

    const getStockFilterUrl = (shopId, stockType) => {
        const filterProp = stockType === "red" ? "foreignShopStockRed" : "foreignShopStockYellow"
        return `/pricetable/${shopId}/Shop/${filterProp}`
    }

    return (
        <div className="card h-100">
            <div className="card-body">
                <div className="row align-items-center mb-3">
                    <div className="col">
                        <h3 className="card-title mb-0">{t("statistic.opportunity_title")}</h3>
                    </div>
                    <div className="col-auto">
                        {shopOptions.length > 0 && (
                            <PPDefaultSelector
                                id="selectStatsOppShops"
                                isMulti={true}
                                closeMenuOnSelect={false}
                                onChange={setSelectedShops}
                                data={shopOptions}
                                value={selectedShops}
                                components={SHOP_MULTI_SELECT_COMPONENTS}
                            />
                        )}
                    </div>
                </div>
                {opps.length === 0 ? (
                    <div className="pp-stats-empty">–</div>
                ) : (
                    <>
                        <div className="pp-stats-opp-legend">
                            <span className="legend-item"><span className="dot green"></span> {t("statistic.stock_available")}</span>
                            <span className="legend-item"><span className="dot yellow"></span> {t("statistic.stock_limited")}</span>
                            <span className="legend-item"><span className="dot red"></span> {t("statistic.stock_unavailable")}</span>
                        </div>
                        <AnimatedBarGrid itemKeys={opps.map(r => `${r.shop.shopId}-${r.shop.lang}`)}>
                            {(isReady) => (
                                <div className="pp-stats-opp-list">
                                    {opps.map((row, rowIdx) => {
                                        const yellowPct = row.total > 0 ? Math.round((100 * row.yellow) / row.total) : 0
                                        const redPct = row.total > 0 ? Math.round((100 * row.red) / row.total) : 0
                                        const key = `${row.shop.shopId}-${row.shop.lang}`
                                        const ready = isReady(key)
                                        return (
                                            <div className="pp-stats-opp-row" key={key}>
                                                <div className="pp-stats-opp-head">
                                                    <strong>
                                                        {row.shop.lang && <span className={"fi fi-" + row.shop.lang} style={{ marginRight: "6px" }} />}
                                                        {PPStatisticsInsights.shopLabel(row.shop)}
                                                    </strong>
                                                    <a
                                                        href={getStockFilterUrl(row.shop.shopId, "red")}
                                                        className="pp-stats-link-btn"
                                                        title={t("statistic.opportunity_show_products")}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {t("statistic.opportunity_show_products")} →
                                                    </a>
                                                </div>
                                                <div className="pp-stats-opp-bar-row">
                                                    <div className="pp-stats-opp-bar-stacked">
                                                        <div className="seg green" title={`${row.greenPct}%`} style={{ width: `${row.greenPct}%`, transform: ready ? "scaleX(1)" : "scaleX(0)", transitionDelay: `${rowIdx * 30}ms` }}>
                                                            {row.greenPct >= 10 && <span>{row.greenPct}%</span>}
                                                        </div>
                                                        <div className="seg yellow" title={`${yellowPct}%`} style={{ width: `${yellowPct}%`, transform: ready ? "scaleX(1)" : "scaleX(0)", transitionDelay: `${rowIdx * 30 + 10}ms` }}>
                                                            {yellowPct >= 10 && <span>{yellowPct}%</span>}
                                                        </div>
                                                        <div className="seg red" title={`${redPct}%`} style={{ width: `${redPct}%`, transform: ready ? "scaleX(1)" : "scaleX(0)", transitionDelay: `${rowIdx * 30 + 20}ms` }}>
                                                            {redPct >= 10 && <span>{redPct}%</span>}
                                                        </div>
                                                    </div>
                                                    <div className="pp-stats-opp-count">
                                                        {row.red} / {row.shop.countMappedProducts != null ? row.shop.countMappedProducts : "–"}
                                                    </div>
                                                </div>
                                                <div className="pp-stats-opp-meta">
                                                    <span className="not-green-text">
                                                        {row.notGreenPct}% {t("statistic.opportunity_limited")}
                                                    </span>
                                                    <span className="count-text">
                                                        ({row.yellow + row.red} {t("statistic.trend_products")})
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </AnimatedBarGrid>
                    </>
                )}
            </div>
        </div>
    )
}

function ChartProductMapping(props) {
    const [t, i18n] = useTranslation()
    const auth = useRequireAuth()
    const defaultLanguageStats = localStorage.getItem("default_language_stats")
    const [langFilter, setLangFilter] = useState([
        {
            label: countries.getName(defaultLanguageStats, i18n.language),
            value: defaultLanguageStats,
            lang: defaultLanguageStats
        }
    ])

    if (!Array.isArray(props.shopsProductAllCounts) || props.shopsProductAllCounts.length === 0) {
        return t("errors.no_correct_server_data")
    }

    const countKey = "countProducts" in props.shopsProductAllCounts[0] ? "countProducts" : "countMappedProducts"
    const shopStatsForLang = (Array.isArray(props.data) ? props.data : []).filter((x) => x != null)
    const languages = uniqBy(
        shopStatsForLang.map((x) => ({
            label: countries.getName(x.lang, "de"),
            value: x.lang,
            lang: x.lang
        })),
        (e) => e.value
    )

    const series = props.shopsProductAllCounts.filter((shop) => {
        if (langFilter && langFilter.length > 0) {
            return langFilter?.map((x) => x.value).includes(shop.lang)
        }
        return true
    })

    const series_data = [{ data: series.map((x) => x[countKey]) }]
    const series_options = {
        chart: { type: "bar" },
        colors: ["#33b2df"],
        plotOptions: {
            bar: {
                barHeight: "100%",
                distributed: true,
                horizontal: true,
                borderRadius: 4,
                dataLabels: { position: "bottom" }
            }
        },
        dataLabels: {
            enabled: true,
            textAnchor: "start",
            style: { colors: ["#111"] },
            formatter: function (val, opt) {
                return opt.w.globals.labels[opt.dataPointIndex] + ": " + val
            },
            offsetX: 0,
            dropShadow: { enabled: false }
        },
        stroke: { width: 1, colors: ["#fff"] },
        xaxis: { categories: series.map((x) => "(" + x.lang + ") " + x.name) },
        yaxis: { show: false },
        legend: { show: false },
        tooltip: { enabled: false, theme: "dark" }
    }

    if (props.pushCustomerProducts) {
        let customerName = auth?.login?.customer?.name
        if (auth?.login?.token_name) {
            customerName = auth?.login?.token_name
        }
        series_data[0].data.unshift(props.pushCustomerProducts)
        series_options.xaxis.categories.unshift(customerName)
        series_options.colors = series_data[0].data.map(() => "#33b2df")
        series_options.colors[0] = "#007bff"
        series_options.dataLabels.style.colors = series_data[0].data.map(() => "#111")
    }

    return (
        <>
            <div className="row">
                <div className="col card-header ui-sortable-handle">
                    <h3 className="card-title">
                        <i className="fas fa-cubes me-1" />
                        {props.title}
                    </h3>
                </div>
            </div>
            <div className="mt-3">
                <PPDefaultSelector
                    id={props.id}
                    isMulti={true}
                    onChange={setLangFilter}
                    data={languages}
                    defaultValue={langFilter}
                    components={{ MultiValueLabel, Option }}
                />
            </div>
            <div className="card-body">
                <ReactApexChart
                    options={series_options}
                    series={series_data}
                    type="bar"
                    height={barHeight(series_data[0]?.data?.length)}
                />
            </div>
        </>
    )
}

function parseCrawlDay(day) {
    const [d, m, y] = String(day).split(".").map(Number)
    return new Date(y, m - 1, d).getTime()
}

function CrawlCharts({ crawlerCountSeries, t }) {
    const prepareSeries = () => {
        if (crawlerCountSeries == null || crawlerCountSeries.length === 0) {
            return []
        }
        const sorted = crawlerCountSeries
            .map((point) => ({ x: point.x, y: Number.parseInt(point.y) }))
            .sort((a, b) => parseCrawlDay(a.x) - parseCrawlDay(b.x))
        const last14 = sorted.slice(-14)
        const thisWeek = last14.slice(-7)
        const lastWeek = last14.slice(0, -7)
        if (lastWeek.length > 0) {
            return [
                { name: t("statistic.crawl_this_week"), data: thisWeek },
                { name: t("statistic.crawl_last_week"), data: lastWeek }
            ]
        }
        return [{ name: t("statistic.crawl_this_week"), data: thisWeek }]
    }
    const series = prepareSeries()
    const areaOptions = {
        xaxis: {
            type: "category",
            labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: "11px" } }
        },
        chart: { toolbar: { show: false } },
        dataLabels: { enabled: false },
        colors: [COLORS.NeutralStart, COLORS.OrangeStart],
        stroke: { curve: "smooth" },
        fill: { type: "gradient", gradient: { opacityFrom: 0.55, opacityTo: 0.15 } }
    }
    const barOptions = {
        chart: { toolbar: { show: false } },
        dataLabels: { enabled: true },
        colors: [COLORS.NeutralStart, COLORS.OrangeStart],
        xaxis: {
            type: "category",
            labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: "11px" } }
        }
    }

    return (
        <div className="card">
            <div className="card-header border-0">
                <h3 className="card-title">
                    <i className="fas fa-chart-area me-1" />
                    {t("statistic.crawl_title")}
                </h3>
                <div className="card-tools">
                    <ul className="nav nav-pills ms-auto" role="tablist">
                        <li className="nav-item">
                            <a className="nav-link active" data-bs-toggle="pill" href="#pp-stats-crawl-area" role="tab">
                                {t("single.area")}
                            </a>
                        </li>
                        <li className="nav-item">
                            <a className="nav-link" data-bs-toggle="pill" href="#pp-stats-crawl-bar" role="tab">
                                {t("single.bar")}
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="card-body">
                {series.length === 0 ? (
                    <div className="pp-stats-empty">{t("statistic.empty_crawls")}</div>
                ) : (
                    <div className="tab-content">
                        <div className="tab-pane fade show active" id="pp-stats-crawl-area" role="tabpanel">
                            <ReactApexChart options={areaOptions} series={series} type="area" height={240} />
                        </div>
                        <div className="tab-pane fade" id="pp-stats-crawl-bar" role="tabpanel">
                            <ReactApexChart options={barOptions} series={series} type="bar" height={240} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export { MarketActivitySummary, CompetitorCheaperChart, PriceBalancePanel, StackedShopChart, VolatilityChart, CategoryRisingChart, CategoryFallingChart, TrendDistributionDonut, StockHistoryChart, OpportunityPanel, ChartProductMapping, CrawlCharts }
